import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import gsap from 'gsap';
import { motion, useScroll, useTransform, useSpring, useInView } from "framer-motion";

import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Activity, Users, Building, Globe } from "lucide-react";
import Carousal from '../components/Carousal/carousal'
// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export default function Component() {
  const [isLoaded, setIsLoaded] = useState(false);
 const containerRef = useRef(null);
   const headerRef = useRef(null);
   const isHeaderInView = useInView(headerRef);
 
   const { scrollYProgress } = useScroll({
     target: containerRef,
     offset: ["start start", "end end"]
   });
   
   const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
   const y = useTransform(scrollYProgress, [0, 1], [0, -300]);
   const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);
 
  const textRef = useRef(null);
  const storyRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    // Initial animations for hero section with updated timing
    gsap.fromTo('.hero-title', {
      opacity: 0,
      y: 100
    }, {
      opacity: 1,
      y: 0,
      duration: 1.5,
      ease: 'power3.out'
    });

    gsap.fromTo('.hero-description', {
      opacity: 0,
      y: 50
    }, {
      opacity: 1,
      y: 0,
      duration: 1.5,
      delay: 0.3,
      ease: 'power3.out'
    });

    // Stats animation
    gsap.fromTo('.stat-item', {
      opacity: 0,
      y: 20
    }, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.1,
      ease: 'power2.out',
      delay: 0.5
    });
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Set initial states
      gsap.set([headerRef.current, textRef.current, storyRef.current, timelineRef.current], { 
        opacity: 1, 
        y: 0 
      });

      // Parallax effect for hero background
      gsap.to('.hero-background', {
        y: -300,
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });

      setIsLoaded(true);
    });

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="relative bg-white overflow-hidden">
           {/* Hero Section with Parallax */}
           <div className="relative h-[80vh] lg:h-[70vh] flex items-center justify-center overflow-hidden">
        <motion.div 
          style={{ y, opacity, scale }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-orange-100/30 via-orange-50/20 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,146,60,0.07),transparent_70%)]" />
        </motion.div>

        <div ref={headerRef} className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-center"
          >
            <motion.div
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="mb-8"
            >
              <h1 className="text-5xl md:text-7xl lg:text-6xl font-bold mb-6">
                Pioneering
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-500">
                  {" "}Moments
                </span>
                
              </h1>
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.5, delay: 0.5 }}
                className="h-1 bg-gradient-to-r from-orange-500 to-yellow-400 mx-auto rounded-full mb-6"
              />
            </motion.div>
            
            <p className="text-xl md:text-2xl lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Each milestone in our journey is a testament to our unwavering perseverance, bold innovation, and relentless pursuit of excellence.
            </p>
          </motion.div>
        </div>
      </div>

    

      {/* Carousel Section */}
      <div className="container mx-auto px-4 py-24" >
        <div ref={headerRef} className="content-section">
        <Carousal/>
        </div>
      </div>

      
    </div>
  );
}